/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {BadgeInfo, Building2, Globe2, Mail, MapPin, Phone} from 'lucide-react';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { CONTACT_DETAILS } from '@/config/footerContent';

function ContactTab() {
    const { t } = useI18n();

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-2">
                <h2>{t('Contact')}</h2>
                <p>{t('Get in touch with EA Systems Dresden GmbH using the contact details below.')}</p>
            </section>

            <section className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                <article className="dashboard-panel items-stretch justify-start p-0">
                    <div className="flex flex-col gap-5 p-6 w-full h-full">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex min-w-0 items-start gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                                    <Building2 className="h-6 w-6" aria-hidden="true" />
                                </div>
                                <div className="min-w-0 space-y-1">
                                    <h3>{CONTACT_DETAILS.company}</h3>
                                    <p>{t('Head office and primary contact')}</p>
                                </div>
                            </div>
                            <div className="contact-company-logo" aria-hidden="true">
                                <img
                                    className="contact-company-logo__image contact-company-logo__image--light"
                                    src="/logo/easd_logo_light.png"
                                    alt=""
                                />
                                <img
                                    className="contact-company-logo__image contact-company-logo__image--dark"
                                    src="/logo/easd_logo_dark.png"
                                    alt=""
                                />
                            </div>
                        </div>

                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-base-content/80">
                                    <MapPin className="h-4 w-4" aria-hidden="true" />
                                    {t('Address')}
                                </div>
                                <div className="space-y-1 text-sm text-base-content/80">
                                    {CONTACT_DETAILS.addressLines.map((addressLine) => (
                                        <div key={addressLine}>{addressLine}</div>
                                    ))}
                                </div>
                            </div>

                            <div className="grid gap-4">
                                <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-base-content/80">
                                        <Phone className="h-4 w-4" aria-hidden="true" />
                                        {t('Phone')}
                                    </div>
                                    <a
                                        className="link link-hover text-sm"
                                        href={`tel:${CONTACT_DETAILS.phone.replace(/\s+/g, '')}`}
                                    >
                                        {CONTACT_DETAILS.phone}
                                    </a>
                                </div>

                                <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-base-content/80">
                                        <Mail className="h-4 w-4" aria-hidden="true" />
                                        {t('Email')}
                                    </div>
                                    <a
                                        className="link link-hover text-sm"
                                        href={`mailto:${CONTACT_DETAILS.email}`}
                                    >
                                        {CONTACT_DETAILS.email}
                                    </a>
                                </div>

                                <div className="rounded-box border border-base-300 bg-base-100/70 p-4">
                                    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-base-content/80">
                                        <Globe2 className="h-4 w-4" aria-hidden="true" />
                                        {t('Website')}
                                    </div>
                                    <a
                                        className="link link-hover text-sm"
                                        href="https://www.ea-energie.de/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        www.ea-energie.de
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </article>

                <article className="dashboard-panel items-stretch justify-start p-0">
                    <div className="flex flex-col gap-5 p-6 w-full h-full">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-secondary">
                                <BadgeInfo className="h-6 w-6" aria-hidden="true" />
                            </div>
                            <div className="space-y-1">
                                <h3>{t('Quick overview')}</h3>
                                <p>{t('Head office and primary contact')}</p>
                            </div>
                        </div>

                        <div className="stats stats-vertical border border-base-300 bg-base-100/70">
                            <div className="stat">
                                <div className="stat-title">{t('Company')}</div>
                                <div className="stat-value text-lg">{CONTACT_DETAILS.company}</div>
                            </div>
                            <div className="stat">
                                <div className="stat-title">{t('Location')}</div>
                                <div className="stat-value text-lg">{CONTACT_DETAILS.city}</div>
                            </div>
                            <div className="stat">
                                <div className="stat-title">{t('Country')}</div>
                                <div className="stat-value text-lg">{CONTACT_DETAILS.country}</div>
                            </div>
                        </div>
                    </div>
                </article>
            </section>
        </div>
    );
}

export default ContactTab;
